import { useEffect, useCallback, useRef } from "react";
import { useStore } from "@/stores/useStore";
import type { SupportedLanguage, Station } from "../../types/geo.types";

export interface UseGeoStationsOptions {
  citySlug?: string;
  lang: SupportedLanguage;
}

export interface UseGeoStationsResult {
  stations: Station[];
  searchResults: Station[];
  loading: boolean;
  error: string | null;
  loadStations(): Promise<void>;
  searchStations(query: string): Promise<void>;
}

/**
 * Hook for accessing station data with search capability.
 *
 * IMPORTANT: This hook should be used in components wrapped with `observer()`.
 * The MobX Map values are accessed directly (no useMemo) so that the observer
 * can track changes to the Map contents and trigger re-renders automatically.
 */
export function useGeoStations(
  options: UseGeoStationsOptions,
): UseGeoStationsResult {
  const { geoStore } = useStore();
  const { citySlug, lang } = options;

  // Track the last search query for accessing results
  const lastSearchQueryRef = useRef<string>("");

  const loadStations = useCallback(async () => {
    if (citySlug) {
      await geoStore.loadStations({ citySlug, lang });
    }
  }, [geoStore, citySlug, lang]);

  const searchStations = useCallback(
    async (query: string) => {
      lastSearchQueryRef.current = query;
      await geoStore.searchStations(query, { citySlug, lang });
    },
    [geoStore, citySlug, lang],
  );

  useEffect(() => {
    loadStations();
  }, [loadStations]);

  // Access MobX Map values directly (observer() handles reactivity)
  // Do NOT wrap in useMemo - it breaks MobX reactivity because Map references don't change
  const stationsData = citySlug
    ? geoStore.stationsByCity.get(`${lang}:${citySlug}`)
    : undefined;
  const stations: Station[] = Array.isArray(stationsData) ? stationsData : [];

  // Search results use the last query to construct the key
  const searchKey = `${lang}:${citySlug ?? "all"}:${lastSearchQueryRef.current}`;
  const searchData = geoStore.stationSearchResults.get(searchKey);
  const searchResults: Station[] = Array.isArray(searchData) ? searchData : [];

  return {
    stations,
    searchResults,
    loading: geoStore.loading,
    error: geoStore.error,
    loadStations,
    searchStations,
  };
}
